import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { tabulatorGet, downloadBlobGet, fetchTextGet, getRequest } from '../../../api/ApiController';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import '../../../assets/css/style.css';
import '../../../assets/css/iconsmenu.css';
import { Dialog } from 'primereact/dialog';
import { PanelMenu } from 'primereact/panelmenu';
import { Checkbox } from 'primereact/checkbox';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { MdSearch } from "react-icons/md";
import { MdSearchOff } from "react-icons/md";
import { EditIcon, PrintIcon, SearchIcon, ExportIcon, RefreshIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, EditRowIcon } from "../../svg/svgIcons";
import { DateTime } from "luxon";
import UtilContext from '../../../components/UtilContext';
import { OverflowMenuProvider } from '../../../components/toolbar/OverflowMenu';
import IntersectionObserverWrapper from '../../../components/toolbar/IntersectionObserverWrap';
import { scrollToFirst as scrollToFirstUtil, scrollUp as scrollUpUtil, scrollDown as scrollDownUtil, scrollToLast as scrollToLastUtil } from '../../../utils/tabulatorScroll';
import { resetTabulatorMask } from '../../../utils/tabulatorResetMask';
import { LocaleContext } from '../../../components/LocaleContext';
import { getEffectiveMask } from '../../../utils/effectiveMask';
import { getModuleSettingValue } from '../../../utils/moduleSettings';
import { cleanSorters, coerceHeaderFilterList, extractSafePaginationParams, createDedupedAjaxRequest, safeParse, parseSorters, parseMaskFilters, mergePagedStandardListFiltersAndSorters, sanitizeTabulatorLayoutAttributes, TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS } from '../../../utils/tabulatorProgressiveLoading';
import { dateUserHeaderFilter } from '../../../utils/dateUserHeaderFilter';
import { inputOnEnterHeaderFilter } from '../../../utils/inputOnEnterHeaderFilter';
import { tabulatorColumnMinWidthFromTitle, tabulatorDateUserColumnMinWidth } from '../../../utils/tabulatorColumnMinWidth';
import { buildTabulatorIdColumn } from '../../../utils/tabulatorIdColumn';
import { TABULATOR_AUDIT_MIN_ROW_HEIGHT, TABULATOR_AUDIT_PROFILES, buildAuditTabulatorColumns } from '../../../utils/tabulatorAuditColumns';
import {
  CALIBRATION_COMPANIES_UPDATED_EVENT,
  SESSION_SAFE_UPDATE,
  applyPagedMutationOverlay,
  classifyCalibrationCompanyListMutation,
  collectCalibrationCompanyChangedFields,
} from '../../../utils/calibrationCompanyListSync';
import { CALIBRATION_COMPANY_CERT_STATE_UPDATED_EVENT } from '../../../utils/calibrationCertificateStateSync';
import {
  CALIBRATION_BY_COMPANY_PAGED_URL,
  CALIBRATION_BY_COMPANY_PRINT_URL,
  CALIBRATION_BY_COMPANY_XLSX_URL,
  applyCalibrationByCompanyHeaderFiltersToParams,
  classifyCalibrationByCompanyCertMutation,
  cleanCalibrationByCompanySorters,
  decorateCalibrationByCompanyPagedRows,
  isCalibrationByCompanySortableField,
  calibrationByCompanyMutationCompanyId,
  calibrationByCompanyShowUrl,
} from '../../../utils/calibrationCompanyCertRecordListSession';
import { pinCalibrationCompanyLeadingColumns } from '../../../utils/calibrationCompanyListMasks';
import { buildExportQueryStringFromParams, openPrintHtmlWindow, triggerBlobDownload } from '../../../utils/serviceTaskExport';
import { EXPORT_DOCUMENT_TITLE_KEYS, xlsxDownloadFilename } from '../../../utils/exportExcelNaming';
import PrintOptionsDialog from '../../../components/PrintOptionsDialog';
import { getPusherChannel } from '../../../utils/pusherChannel';
import {
  applyQualityPartnerListLocale,
  formatQualityPartnerTypeText,
  qualityPartnerStatusHeaderFilter,
  qualityPartnerTypeHeaderFilter,
  qualityPartnerTypeSorter,
  setQualityPartnerTranslator,
} from '../../../utils/qualityPartnerFormatters';
import { uiT } from '../../../utils/uiTranslate';

const PAGE_LIST_KEY = 'CalibrationCompanyCertRecordList';
const DETAIL_PAGE = 'CalibrationCompanyCertRecordDetail';
const DETAIL_MASK = 'calb011';


/** Poradie = poradie stĺpcov v tabuľke aj v print exporte (ID sa netlačí). */
const DEFAULT_COLUMNS_VISIBLE = Object.freeze({
  id: true,
  name: true,
  defined_id: true,
  types: true,
  address: true,
  tel_number: true,
  fax: true,
  email: true,
  web_address: true,
  contact_person: true,
  description: true,
  company_status_display: true,
  created_at: true,
  updated_at: true,
});

function mergeVisibility(saved) {
  const merged = { ...DEFAULT_COLUMNS_VISIBLE };
  if (saved && typeof saved === 'object') {
    Object.keys(DEFAULT_COLUMNS_VISIBLE).forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(saved, k)) merged[k] = saved[k] !== false;
    });
  }
  if (!Object.values(merged).some(Boolean)) return { ...DEFAULT_COLUMNS_VISIBLE };
  return merged;
}

function applyDataColumnVisibilityToTable(table, visibilityMap) {
  if (!table) return;
  for (const f of Object.keys(DEFAULT_COLUMNS_VISIBLE)) {
    if (visibilityMap[f] === false) table.hideColumn?.(f);
    else table.showColumn?.(f);
  }
  setTimeout(() => table.redraw?.(true), 0);
}

function layoutAttributesIncludeId(attributes) {
  if (!Array.isArray(attributes)) return false;
  return attributes.some((node) => node && typeof node === 'object' && node.field === 'id');
}

function byCompanyHeaderFiltersEquivalent(a, b) {
  const norm = (list) => coerceHeaderFilterList(list)
    .filter((filter) => filter?.field && String(filter.value ?? '').trim() !== '')
    .map((filter) => `${filter.field}\0${String(filter.value).trim()}`)
    .sort()
    .join('\n');
  return norm(a) === norm(b);
}

function byCompanySortersEquivalent(a, b) {
  const norm = (list) => cleanSorters(list || [])
    .map((sorter) => `${sorter.field}\0${sorter.dir || 'asc'}`)
    .join('\n');
  return norm(a) === norm(b);
}

function unwrapCompanyResponse(res) {
  return res?.data?.data ?? res?.data ?? res;
}

export default function CalibrationCompanyCertRecordList({ preview = null }) {
  const [state, dispatch] = useContext(DatabaseContext);
  const { locale, t } = useContext(LocaleContext);
  setQualityPartnerTranslator(t);
  const { setUserMaskFilter, openFavoriteDialog, toast, setFastView, getDateFormat, formatDate, getUserColor, getUserInitials, openChosenNotificationPreferences, setDialogText, setVisible } = useContext(UtilContext);
  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;

  const moduleSettings = useMemo(() => state.module_settings || [], [state.module_settings]);
  const showExcludedCompanies = getModuleSettingValue(moduleSettings, 'calibration', 'show_excluded_calibration_companies_list_by_company', '0');
  const showDeactivatedCerts = getModuleSettingValue(moduleSettings, 'calibration', 'show_deactivated_certs_list_by_company', '0');

  const pageSizePc = useMemo(() => {
    const v = moduleSettings.find(s => s.module === 'global' && s.key === 'page_size_pc')?.value;
    const n = parseInt(v, 10);
    return (Number.isFinite(n) && n > 0) ? n : 50;
  }, [moduleSettings]);

  const [editIcon, setEditIcon] = useState(true);
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchIconColor, setSearchIconColor] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [markUnmark, setMarkUnmarked] = useState(true);
  const [columnsVisible, setColumnsVisible] = useState({ ...DEFAULT_COLUMNS_VISIBLE });
  const [maskVisible, setMaskVisible] = useState(false);
  const [printChoiceVisible, setPrintChoiceVisible] = useState(false);
  const [printOrientationChoice, setPrintOrientationChoice] = useState('landscape');
  const [printLayoutChoice, setPrintLayoutChoice] = useState('branded');
  const [tableSessionKey, setTableSessionKey] = useState(0);

  const ref = useRef(null);
  const overlayRef = useRef(null);
  const inFlightRef = useRef({ sig: null, promise: null });
  const completedCacheRef = useRef(new Map());
  const tableBuiltRef = useRef(false);
  const lastResponseRef = useRef(null);
  const isRestoringRef = useRef(false);
  const userInteractedRef = useRef(false);
  const savedHScrollRef = useRef({ left: 0, pending: false });
  const lastContainerHeightRef = useRef(0);
  const submittedSearchValueRef = useRef('');
  const pendingInvalidationRef = useRef(false);
  const mutationOverlayRef = useRef(new Map());
  const listMountedRef = useRef(true);
  const restartQueuedRef = useRef(false);
  const ajaxGenerationRef = useRef(0);
  const showGenerationRef = useRef(0);
  const confirmedQueryRef = useRef({ filters: [], sorters: [] });
  const sessionQueryActiveRef = useRef(false);
  const tableContainerRef = useRef(null);
  const showFlagsRef = useRef(`${showExcludedCompanies}|${showDeactivatedCerts}`);
  const showDeactivatedCertsRef = useRef(showDeactivatedCerts);
  showDeactivatedCertsRef.current = showDeactivatedCerts;
  const showExcludedCompaniesRef = useRef(showExcludedCompanies);
  showExcludedCompaniesRef.current = showExcludedCompanies;

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);
  const isTableVisible = useCallback(() => {
    const el = tableContainerRef.current;
    if (el && el.offsetParent != null && el.clientHeight > 0) return true;
    const table = getTable();
    const tel = table?.getElement?.() ?? table?.element;
    return !!(tel && tel.offsetParent != null && tel.clientHeight > 0);
  }, [getTable]);

  const snapshotConfirmedQuery = useCallback(() => {
    const table = getTable();
    confirmedQueryRef.current = {
      filters: coerceHeaderFilterList(table?.getHeaderFilters?.()),
      sorters: cleanSorters(table?.getSorters?.() || []),
    };
  }, [getTable]);

  const restartFullProgressiveSession = useCallback(() => {
    if (!listMountedRef.current || restartQueuedRef.current) return;
    snapshotConfirmedQuery();
    sessionQueryActiveRef.current = true;
    ajaxGenerationRef.current += 1;
    showGenerationRef.current += 1;
    pendingInvalidationRef.current = false;
    mutationOverlayRef.current = new Map();
    restartQueuedRef.current = true;
    completedCacheRef.current.clear();
    lastResponseRef.current = null;
    inFlightRef.current = { sig: null, promise: null };
    tableBuiltRef.current = false;
    isRestoringRef.current = true;
    const table = getTable();
    if (typeof table?.destroy === 'function') table.destroy();
    ref.current = null;
    setTableSessionKey((key) => key + 1);
  }, [getTable, snapshotConfirmedQuery]);

  const requestFullProgressiveSessionRestart = useCallback(() => {
    if (!listMountedRef.current) return;
    if (!isTableVisible()) {
      pendingInvalidationRef.current = true;
      return;
    }
    restartFullProgressiveSession();
  }, [isTableVisible, restartFullProgressiveSession]);

  const getLoadedRowComponent = useCallback((id) => {
    const table = getTable();
    if (!table || typeof table.getRow !== 'function' || id == null || id === '') return null;
    try {
      const row = table.getRow(id);
      return row && row !== false ? row : null;
    } catch (_e) {
      return null;
    }
  }, [getTable]);

  const applyCompanyRowPatch = useCallback((rowData) => {
    const decorated = decorateCalibrationByCompanyPagedRows([rowData])[0] || rowData;
    const id = decorated?.id;
    const rowComponent = getLoadedRowComponent(id);
    if (!rowComponent || typeof rowComponent.update !== 'function') {
      requestFullProgressiveSessionRestart();
      return;
    }
    rowComponent.update(decorated);
    mutationOverlayRef.current.set(String(id), decorated);
  }, [getLoadedRowComponent, requestFullProgressiveSessionRestart]);

  /** Kompozitný show firmy vrátane vnorených OTK certifikátov – bez `paged_by_company` requestu. */
  const patchCompanyFromCompositeShow = useCallback((companyId) => {
    if (companyId == null || companyId === '') {
      requestFullProgressiveSessionRestart();
      return;
    }
    const generation = showGenerationRef.current;
    getRequest(calibrationByCompanyShowUrl(companyId, { showExcludedCompanies: showExcludedCompaniesRef.current, showDeactivatedCerts: showDeactivatedCertsRef.current })).then((res) => {
      if (generation !== showGenerationRef.current) return;
      const rowData = unwrapCompanyResponse(res);
      if (!rowData?.id) {
        requestFullProgressiveSessionRestart();
        return;
      }
      applyCompanyRowPatch(rowData);
    }).catch(() => requestFullProgressiveSessionRestart());
  }, [applyCompanyRowPatch, requestFullProgressiveSessionRestart]);

  const captureHScroll = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    if (!holder) return;
    savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
  }, [getTable]);

  const restoreHScroll = useCallback(() => {
    if (!savedHScrollRef.current.pending) return;
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    const header = table.columnManager?.contentsElement;
    if (!holder) return;
    const left = savedHScrollRef.current.left;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        holder.scrollLeft = left;
        if (header) header.scrollLeft = left;
        savedHScrollRef.current.pending = false;
      });
    });
  }, [getTable]);

  useEffect(() => {
    listMountedRef.current = true;
    return () => { listMountedRef.current = false; };
  }, []);

  useEffect(() => {
    const next = `${showExcludedCompanies}|${showDeactivatedCerts}`;
    if (showFlagsRef.current === next) return;
    showFlagsRef.current = next;
    requestFullProgressiveSessionRestart();
  }, [showExcludedCompanies, showDeactivatedCerts, requestFullProgressiveSessionRestart]);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const h = el.clientHeight;
      const visible = h > 0 || isTableVisible();
      if (visible) {
        const wasHidden = lastContainerHeightRef.current <= 0;
        lastContainerHeightRef.current = Math.max(h, 1);
        if (pendingInvalidationRef.current) {
          requestAnimationFrame(() => restartFullProgressiveSession());
        } else if (wasHidden) {
          requestAnimationFrame(() => getTable()?.redraw?.(true));
        }
      } else {
        lastContainerHeightRef.current = 0;
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [getTable, isTableVisible, restartFullProgressiveSession]);

  const sessionContext = useCallback(() => ({
    toolbarSearch: submittedSearchValueRef.current,
    activeHeaderFilters: coerceHeaderFilterList(getTable()?.getHeaderFilters?.()),
    activeSorters: cleanSorters(getTable()?.getSorters?.() || []),
    isRowLoaded: (id) => Boolean(getLoadedRowComponent(id)),
  }), [getLoadedRowComponent, getTable]);

  /** R3A – zmeny firmy (qlty026/032 writers). */
  useEffect(() => {
    const onUpdated = (event) => {
      const decision = classifyCalibrationCompanyListMutation(event, sessionContext());
      if (decision == null) return;
      if (decision === SESSION_SAFE_UPDATE) {
        patchCompanyFromCompositeShow(event?.detail?.id ?? event?.detail?.row?.id);
        return;
      }
      requestFullProgressiveSessionRestart();
    };
    window.addEventListener(CALIBRATION_COMPANIES_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(CALIBRATION_COMPANIES_UPDATED_EVENT, onUpdated);
  }, [patchCompanyFromCompositeShow, requestFullProgressiveSessionRestart, sessionContext]);

  /** R3B – zmeny certifikátu / záznamu (qlty030 writers); riadok je firma, nie certifikát. */
  useEffect(() => {
    const onCertState = (event) => {
      const decision = classifyCalibrationByCompanyCertMutation(event, sessionContext());
      if (decision === SESSION_SAFE_UPDATE) {
        patchCompanyFromCompositeShow(calibrationByCompanyMutationCompanyId(event?.detail));
        return;
      }
      requestFullProgressiveSessionRestart();
    };
    window.addEventListener(CALIBRATION_COMPANY_CERT_STATE_UPDATED_EVENT, onCertState);
    return () => window.removeEventListener(CALIBRATION_COMPANY_CERT_STATE_UPDATED_EVENT, onCertState);
  }, [patchCompanyFromCompositeShow, requestFullProgressiveSessionRestart, sessionContext]);

  useEffect(() => {
    const channel = getPusherChannel();
    if (!channel) return;
    const invalidate = () => requestFullProgressiveSessionRestart();
    const onCompanyUpdated = (payload) => {
      const id = payload?.id;
      if (id == null || id === '' || !getLoadedRowComponent(id)) {
        invalidate();
        return;
      }
      const generation = showGenerationRef.current;
      getRequest(calibrationByCompanyShowUrl(id, { showExcludedCompanies: showExcludedCompaniesRef.current, showDeactivatedCerts: showDeactivatedCertsRef.current })).then((res) => {
        if (generation !== showGenerationRef.current) return;
        const rowData = unwrapCompanyResponse(res);
        if (!rowData?.id) {
          invalidate();
          return;
        }
        const loadedRow = getLoadedRowComponent(rowData.id);
        const previous = typeof loadedRow?.getData === 'function' ? loadedRow.getData() : null;
        const changedFields = collectCalibrationCompanyChangedFields(previous, rowData, { implicitAudit: true });
        const decision = classifyCalibrationCompanyListMutation({
          detail: { operation: 'update', id: rowData.id, row: rowData, changedFields },
        }, sessionContext());
        if (decision === SESSION_SAFE_UPDATE) {
          applyCompanyRowPatch(rowData);
          return;
        }
        invalidate();
      }).catch(() => invalidate());
    };
    const onCertRowChanged = (payload) => {
      const companyId = payload?.company_id;
      if (companyId == null || companyId === '' || !getLoadedRowComponent(companyId)) {
        invalidate();
        return;
      }
      if (String(submittedSearchValueRef.current ?? '').trim() !== '') {
        invalidate();
        return;
      }
      patchCompanyFromCompositeShow(companyId);
    };
    channel.bind('company.created', invalidate);
    channel.bind('company.updated', onCompanyUpdated);
    channel.bind('company.deleted', invalidate);
    channel.bind('company_certificate.created', invalidate);
    channel.bind('company_certificate.updated', onCertRowChanged);
    channel.bind('company_certificate.deleted', invalidate);
    channel.bind('company_certificate.activated', invalidate);
    channel.bind('company_certificate.deactivated', invalidate);
    return () => {
      channel.unbind('company.created', invalidate);
      channel.unbind('company.updated', onCompanyUpdated);
      channel.unbind('company.deleted', invalidate);
      channel.unbind('company_certificate.created', invalidate);
      channel.unbind('company_certificate.updated', onCertRowChanged);
      channel.unbind('company_certificate.deleted', invalidate);
      channel.unbind('company_certificate.activated', invalidate);
      channel.unbind('company_certificate.deactivated', invalidate);
    };
  }, [applyCompanyRowPatch, getLoadedRowComponent, patchCompanyFromCompositeShow, requestFullProgressiveSessionRestart, sessionContext]);

  useEffect(() => {
    setQualityPartnerTranslator(t);
    applyQualityPartnerListLocale(getTable(), t);
  }, [t, locale, getTable]);

  function onEditRow(calibration_company) {
    dispatch({ type: 'create_new_tab', name: uiT('Calibration.CompanyCertValidity'), translationKey: 'Calibration.CompanyCertValidity', legacyLabel: uiT('Calibration.CompanyCertValidity'), page: DETAIL_PAGE, props: { calibration_company, type: 'edit' }, mask: DETAIL_MASK });
  }

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return <div className='flex items-center justify-center w-full h-full'><button type='button' onClick={() => onEditRow(rowData)}><EditRowIcon /></button></div>;
  }

  function TypeFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return formatQualityPartnerTypeText(rowData);
  }

  function CompanyStatusFormatter(props) {
    const rowData = props.cell.getRow().getData();
    const v = rowData?.validity;
    return <span>{v == 1 || v === '1' ? t('Status.Active') : t('Status.Excluded')}</span>;
  }

  /** Remote filtrovanie – hodnota ide na server, lokálne sa nič nezahadzuje. */
  const remotePassthroughFilter = useCallback(() => true, []);

  const idTitle = t('BusinessPartnerList.Column.Id') === 'BusinessPartnerList.Column.Id' ? 'ID' : t('BusinessPartnerList.Column.Id');
  const createdTitle = t('CalibrationCompanyCertStateList.Column.CreatedAt');
  const updatedTitle = t('CalibrationCompanyCertStateList.Column.UpdatedAt');
  const auditTailColumns = useMemo(
    () => buildAuditTabulatorColumns({
      profile: TABULATOR_AUDIT_PROFILES.createdByUpdatedBy,
      includeId: false,
      dateFormat: dateFmt,
      dateTimeFormat: dateTimeFmt,
      getUserColor,
      getUserInitials,
      remotePassthrough: true,
    }).map((col) => (col.field === 'created_at'
      ? { ...col, title: createdTitle, headerSort: isCalibrationByCompanySortableField('created_at') }
      : col.field === 'updated_at'
        ? { ...col, title: updatedTitle, headerSort: isCalibrationByCompanySortableField('updated_at') }
        : col)),
    [dateFmt, dateTimeFmt, getUserColor, getUserInitials, createdTitle, updatedTitle]
  );

  const textColumn = useCallback((field, titleKey) => {
    const title = t(titleKey);
    return {
      title,
      field,
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: remotePassthroughFilter,
      minWidth: tabulatorColumnMinWidthFromTitle(title),
      widthGrow: 1,
      headerSort: isCalibrationByCompanySortableField(field),
    };
  }, [remotePassthroughFilter, t]);

  const columns = useMemo(() => [
    { field: 'rowSelection', formatter: 'rowSelection', titleFormatter: 'rowSelection', hozAlign: 'center', width: 44, minWidth: 44, headerSort: false, resizable: false, frozen: true, print: false, download: false, headerFilter: false },
    { title: '', field: 'editor', hozAlign: 'center', width: 44, minWidth: 44, headerSort: false, formatter: reactFormatter(<EditRowFormatter />), resizable: false, frozen: true, print: false, download: false, headerFilter: false },
    buildTabulatorIdColumn({ frozen: true, headerFilterFunc: remotePassthroughFilter, extra: { title: idTitle, print: false, download: false, headerSort: true } }),
    textColumn('name', 'BusinessPartnerList.Column.Name'),
    textColumn('defined_id', 'BusinessPartnerList.Column.DefinedId'),
    {
      ...textColumn('types', 'BusinessPartnerList.Column.Types'),
      headerFilterFunc: qualityPartnerTypeHeaderFilter,
      formatter: reactFormatter(<TypeFormatter />),
      sorter: qualityPartnerTypeSorter,
      headerSort: isCalibrationByCompanySortableField('types'),
    },
    textColumn('address', 'BusinessPartnerList.Column.Address'),
    textColumn('tel_number', 'BusinessPartnerList.Column.TelNumber'),
    textColumn('fax', 'BusinessPartnerList.Column.Fax'),
    textColumn('email', 'BusinessPartnerList.Column.Email'),
    textColumn('web_address', 'BusinessPartnerList.Column.WebAddress'),
    textColumn('contact_person', 'BusinessPartnerList.Column.ContactPerson'),
    textColumn('description', 'BusinessPartnerList.Column.Description'),
    {
      ...textColumn('company_status_display', 'CalibrationCompanyList.Column.CompanyStatus'),
      headerFilterFunc: qualityPartnerStatusHeaderFilter,
      formatter: reactFormatter(<CompanyStatusFormatter />),
      headerSort: isCalibrationByCompanySortableField('company_status_display'),
    },
    ...auditTailColumns,
  ], [idTitle, textColumn, remotePassthroughFilter, auditTailColumns]);

  const buildRequestParams = useCallback((paramsFromTabulator = {}) => {
    const extra = { ...extractSafePaginationParams(paramsFromTabulator), type: 'calibration' };
    if (extra.size == null) extra.size = pageSizePc;
    extra.show_excluded_companies = showExcludedCompanies;
    extra.show_deactivated_certs = showDeactivatedCerts;
    if (preview != null && preview !== '') extra.preview = preview;
    if (sessionQueryActiveRef.current) {
      applyCalibrationByCompanyHeaderFiltersToParams(extra, coerceHeaderFilterList(confirmedQueryRef.current.filters));
      const confirmedSorters = cleanCalibrationByCompanySorters(confirmedQueryRef.current.sorters || []);
      if (confirmedSorters.length > 0) extra.sorters = JSON.stringify(confirmedSorters);
    } else {
      mergePagedStandardListFiltersAndSorters(extra, {
        getTable: tableBuiltRef.current ? getTable : () => null,
        tableBuiltForParams: tableBuiltRef.current,
        userMaskFilters: state.user_mask_filters,
        maskPage: PAGE_LIST_KEY,
        appendFilter: (filter, target) => applyCalibrationByCompanyHeaderFiltersToParams(target, [filter]),
      });
      const allowed = cleanCalibrationByCompanySorters(safeParse(extra.sorters, []));
      if (allowed.length > 0) extra.sorters = JSON.stringify(allowed);
      else delete extra.sorters;
    }
    const search = String(submittedSearchValueRef.current ?? '').trim();
    if (search !== '') {
      extra.toolbar_search = search;
      extra.search = search;
    }
    return extra;
  }, [getTable, state.user_mask_filters, pageSizePc, preview, showExcludedCompanies, showDeactivatedCerts]);

  const buildRequestParamsRef = useRef(buildRequestParams);
  buildRequestParamsRef.current = buildRequestParams;
  const getBuildRequestParams = useCallback((paramsFromTabulator) => buildRequestParamsRef.current(paramsFromTabulator), []);

  const getPrintColumnsOrder = useCallback(() => {
    const table = getTable();
    const allowed = Object.keys(DEFAULT_COLUMNS_VISIBLE).filter((f) => f !== 'id');
    const fallback = () => allowed.filter((f) => columnsVisible[f] !== false);
    if (!table?.getColumns) return fallback();
    const allowedSet = new Set(allowed);
    const out = [];
    const seen = new Set();
    try {
      table.getColumns().forEach((col) => {
        const field = col.getField?.() ?? col.getDefinition?.()?.field;
        if (!field || !allowedSet.has(field)) return;
        if (columnsVisible[field] === false) return;
        if (seen.has(field)) return;
        seen.add(field);
        out.push(field);
      });
    } catch (_) {
      return fallback();
    }
    return out.length > 0 ? out : fallback();
  }, [getTable, columnsVisible]);

  const buildByCompanyExportQueryString = useCallback((opts = {}) => {
    const extra = {
      type: 'calibration',
      show_excluded_companies: showExcludedCompanies,
      show_deactivated_certs: showDeactivatedCerts,
    };
    mergePagedStandardListFiltersAndSorters(extra, {
      getTable: tableBuiltRef.current ? getTable : () => null,
      tableBuiltForParams: tableBuiltRef.current,
      userMaskFilters: state.user_mask_filters,
      maskPage: PAGE_LIST_KEY,
      appendFilter: (filter, target) => applyCalibrationByCompanyHeaderFiltersToParams(target, [filter]),
    });
    const allowed = cleanCalibrationByCompanySorters(safeParse(extra.sorters, []));
    if (allowed.length > 0) extra.sorters = JSON.stringify(allowed);
    else delete extra.sorters;
    const search = String(submittedSearchValueRef.current ?? '').trim();
    if (search !== '') {
      extra.toolbar_search = search;
      extra.search = search;
    }
    if (preview != null && preview !== '') extra.preview = preview;
    return buildExportQueryStringFromParams(extra, opts);
  }, [getTable, state.user_mask_filters, preview, showExcludedCompanies, showDeactivatedCerts]);

  const runByCompanyPrint = useCallback(async () => {
    try {
      setPrintChoiceVisible(false);
      setDialogText?.(t('Common.PreparingPrint'));
      setVisible?.(true);
      const suffix = buildByCompanyExportQueryString({
        includePrintColumns: true,
        printLayout: printLayoutChoice,
        printOrientation: printOrientationChoice,
        printColumns: getPrintColumnsOrder(),
      });
      const html = await fetchTextGet(`${CALIBRATION_BY_COMPANY_PRINT_URL}${suffix}`);
      if (!openPrintHtmlWindow(html)) {
        toast?.current?.show({ severity: 'warn', summary: t('DepartmentList.Toast.PrintTitle'), detail: t('Common.EnablePopups'), life: 4000 });
      }
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('DepartmentList.Toast.PrintTitle'), detail: e?.message || t('DepartmentList.Toast.PrintFailed'), life: 4500 });
    } finally {
      setVisible?.(false);
    }
  }, [buildByCompanyExportQueryString, getPrintColumnsOrder, printLayoutChoice, printOrientationChoice, setDialogText, setVisible, t, toast]);

  const exportByCompanyXlsx = useCallback(async () => {
    try {
      setDialogText?.(t('Common.PreparingExport'));
      setVisible?.(true);
      const suffix = buildByCompanyExportQueryString({
        includePrintColumns: true,
        printColumns: getPrintColumnsOrder(),
      });
      const defaultXlsx = xlsxDownloadFilename(t(EXPORT_DOCUMENT_TITLE_KEYS.CALIBRATION_COMPANIES));
      const { blob, filename } = await downloadBlobGet(`${CALIBRATION_BY_COMPANY_XLSX_URL}${suffix}`, defaultXlsx);
      triggerBlobDownload(blob, filename || defaultXlsx);
    } catch (e) {
      toast?.current?.show({ severity: 'error', summary: t('DepartmentList.Toast.ExportTitle'), detail: e?.message || t('DepartmentList.Toast.ExportFailed'), life: 4500 });
    } finally {
      setVisible?.(false);
    }
  }, [buildByCompanyExportQueryString, getPrintColumnsOrder, setDialogText, setVisible, t, toast]);

  const restoreHeaderFilters = useCallback((table, rawFilters) => {
    const filterEntries = Array.isArray(rawFilters)
      ? rawFilters
      : (rawFilters && typeof rawFilters === 'object'
        ? Object.entries(rawFilters).map(([field, value]) => ({ field, value }))
        : []);
    filterEntries.forEach((filter) => {
      if (!filter?.field) return;
      const value = filter.value;
      if (value == null || value === '') return;
      const def = table.getColumn?.(filter.field)?.getDefinition?.();
      if (!def?.headerFilter) return;
      table.setHeaderFilterValue?.(filter.field, value, true);
    });
  }, []);

  const searchTable = () => {
    const normalized = String(searchValue ?? '').trim();
    submittedSearchValueRef.current = normalized;
    setSearchIconColor(normalized === '' ? '' : 'active');
    captureHScroll();
    requestFullProgressiveSessionRestart();
    setVisibleSearch(false);
  };

  const clearSearch = () => {
    setSearchIconColor('');
    setSearchValue('');
    submittedSearchValueRef.current = '';
    captureHScroll();
    requestFullProgressiveSessionRestart();
  };

  const onSelectRow = (data) => {
    setEditIcon(!data || data.length === 0);
  };

  const scrollToFirst = () => scrollToFirstUtil(getTable());
  const scrollUp = () => scrollUpUtil(getTable());
  const scrollDown = () => scrollDownUtil(getTable());
  const scrollToLast = () => scrollToLastUtil(getTable());

  function editSelectedCompany() {
    const sel = getTable()?.getSelectedData?.()?.[0];
    if (sel) onEditRow(sel);
  }

  function setMask(item, value) {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE, item)) return;
    const next = { ...DEFAULT_COLUMNS_VISIBLE, ...columnsVisible, [item]: value };
    if (!Object.values(next).some(Boolean)) return;
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  const itemRenderer = (item, options) => (
    <a className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
      {item.shortcut && <span className="ml-auto border-1 surface-border border-round surface-100 text-xs p-1">{item.shortcut}</span>}
    </a>
  );

  const [expandedKeys, setExpandedKeys] = useState({});

  const handleClickOutside = (event) => {
    if (overlayRef.current && !overlayRef.current.contains(event.target)) {
      setExpandedKeys({});
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const resetMask = () => resetTabulatorMask({
    page: PAGE_LIST_KEY,
    defaultColumnsVisible: DEFAULT_COLUMNS_VISIBLE,
    ref,
    columns,
    setColumnsVisible,
    onAfterReset: () => requestFullProgressiveSessionRestart(),
  });

  const items = [
    {
      key: '0',
      label: t('Common.Actions'),
      icon: 'pi pi-sliders-v',
      template: itemRenderer,
      items: [
        {
          key: '0_1',
          label: t('Common.SetFilter'),
          icon: 'pi pi-filter',
          command: () => { userInteractedRef.current = true; setUserMaskFilter(PAGE_LIST_KEY, 'set_filters', null, getTable()?.getHeaderFilters?.() ?? [], null); requestFullProgressiveSessionRestart(); },
          template: itemRenderer,
        },
        {
          key: '0_2',
          label: t('Common.ClearFilter'),
          icon: 'pi pi-filter-slash',
          command: () => { userInteractedRef.current = true; setUserMaskFilter(PAGE_LIST_KEY, 'set_filters', null, [], null); getTable()?.clearHeaderFilter?.(); requestFullProgressiveSessionRestart(); },
          template: itemRenderer,
        },
        {
          key: '0_3',
          label: t('Common.SetSorting'),
          icon: 'pi pi-sort-alt',
          command: () => { userInteractedRef.current = true; setUserMaskFilter(PAGE_LIST_KEY, 'set_sorters', null, null, null, cleanSorters(getTable()?.getSorters?.() ?? [])); OverflowMenuProvider.setVisible(null); setExpandedKeys({}); requestFullProgressiveSessionRestart(); },
          template: itemRenderer,
        },
        {
          key: '0_4',
          label: t('Common.ClearSorting'),
          icon: 'pi pi-sort-alt-slash',
          command: () => { userInteractedRef.current = true; setUserMaskFilter(PAGE_LIST_KEY, 'set_sorters', null, null, null, []); getTable()?.setSort?.([]); OverflowMenuProvider.setVisible(null); setExpandedKeys({}); requestFullProgressiveSessionRestart(); },
          template: itemRenderer,
        },
        {
          key: '0_5',
          label: t('Common.SetMask'),
          icon: 'pi pi-server',
          command: () => { setMaskVisible(true); OverflowMenuProvider.setVisible(null); setExpandedKeys({}); },
          template: itemRenderer,
        },
        {
          key: '0_5b',
          label: t('Common.ResetUserMask'),
          icon: 'pi pi-refresh',
          command: () => { resetMask(); OverflowMenuProvider.setVisible(null); setExpandedKeys({}); },
          template: itemRenderer,
        },
        {
          key: '0_6',
          label: t('Common.Notifications'),
          icon: 'pi pi-envelope',
          command: () => { openChosenNotificationPreferences(PAGE_LIST_KEY); OverflowMenuProvider.setVisible(null); },
          template: itemRenderer,
        },
      ],
    },
  ];

  function changeMarkings(value) {
    const next = { ...DEFAULT_COLUMNS_VISIBLE };
    Object.keys(next).forEach((k) => { next[k] = value; });
    if (!value) next.id = true;
    applyDataColumnVisibilityToTable(getTable(), next);
    setColumnsVisible(next);
  }

  return (
    <div className="h-full min-h-0 flex flex-col overflow-hidden">
      <div className='w-full h-10 flex flex-row bg-[#333] shrink-0'>
        <IntersectionObserverWrapper>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="edit"><button className="disabled:opacity-25" disabled={editIcon} onClick={() => editSelectedCompany()}><EditIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="refresh"><button type="button" onClick={() => requestFullProgressiveSessionRestart()} title={t('Common.Refresh')}><RefreshIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="print"><button type="button" onClick={() => setPrintChoiceVisible(true)}><PrintIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="search">
            <button type="button" title={searchIconColor !== 'active' ? t('Common.Search') : t('ServiceTask.CancelSearch')} onClick={searchIconColor !== 'active' ? () => setVisibleSearch(true) : clearSearch}>
              {searchIconColor === '' ? <MdSearch fill='#fff' className='w-[28px] h-[28px]' aria-hidden="true" /> : <MdSearchOff fill='#fff' className='w-[28px] h-[28px]' aria-hidden="true" />}
            </button>
          </div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="export"><button type="button" onClick={() => exportByCompanyXlsx()} title={t('Common.ExportToExcel')}><ExportIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert"><VerticalLineIcon /></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="first"><button type="button" onClick={() => scrollToFirst()}><BackToFirstIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="back"><button type="button" onClick={() => scrollUp()}><BackIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="forward"><button type="button" onClick={() => scrollDown()}><ForwardIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="last"><button type="button" onClick={() => scrollToLast()}><ForwardToLastIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert2"><VerticalLineIcon /></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="favorite"><button type="button" onClick={() => openFavoriteDialog(uiT('Calibration.CompaniesCertValidity'), PAGE_LIST_KEY)}><FavoriteIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="fast"><button type="button" onClick={() => setFastView(PAGE_LIST_KEY, columnsVisible, columns)}><PriceIcon /></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert3"><VerticalLineIcon /></div>
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>
      <div className='px-1 bg-white rounded-md shadow-md flex-1 min-h-0 overflow-hidden'>
        <div ref={tableContainerRef} className="flex-1 min-h-0 overflow-hidden">
          <ReactTabulator
            key={tableSessionKey}
            className='h-[calc(100vh_-_193px)]'
            onRef={(r) => (ref.current = r?.current ?? r)}
            columns={columns}
            events={{
              rowSelectionChanged: onSelectRow,
              tableBuilt: () => {
                isRestoringRef.current = true;
                userInteractedRef.current = false;
                const table = getTable();
                const restartRestore = sessionQueryActiveRef.current;
                if (!table) {
                  tableBuiltRef.current = true;
                  restartQueuedRef.current = false;
                  snapshotConfirmedQuery();
                  setTimeout(() => {
                    isRestoringRef.current = false;
                    sessionQueryActiveRef.current = false;
                  }, 0);
                  return;
                }
                const effective = getEffectiveMask(PAGE_LIST_KEY, state);
                if (effective?.type === 'user' && effective.userMask) {
                  const userMask = effective.userMask;
                  try {
                    table.blockRedraw?.();
                    const attributes = pinCalibrationCompanyLeadingColumns(
                      sanitizeTabulatorLayoutAttributes(safeParse(userMask.attributes, []), TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS)
                    );
                    if (layoutAttributesIncludeId(attributes) && attributes.length > 1) {
                      table.setColumnLayout?.(attributes);
                    }
                    if (restartRestore) {
                      restoreHeaderFilters(table, confirmedQueryRef.current.filters);
                    } else {
                      restoreHeaderFilters(table, parseMaskFilters(userMask.filters));
                      const sorters = cleanCalibrationByCompanySorters(parseSorters(userMask.sorters)).map((s) => ({
                        column: s.field,
                        dir: s.dir,
                      }));
                      if (sorters.length > 0) table.setSort?.(sorters);
                    }
                    const jsonColumns = safeParse(userMask.columns, {});
                    if (jsonColumns && typeof jsonColumns === 'object' && !Array.isArray(jsonColumns)) {
                      const merged = mergeVisibility(jsonColumns);
                      setColumnsVisible(merged);
                      applyDataColumnVisibilityToTable(table, merged);
                    }
                  } finally {
                    table.restoreRedraw?.(true);
                  }
                } else if (effective?.type === 'standard' && effective.standardMask) {
                  try {
                    table.blockRedraw?.();
                    const attributes = pinCalibrationCompanyLeadingColumns(
                      sanitizeTabulatorLayoutAttributes(safeParse(effective.standardMask.attributes, []), TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS)
                    );
                    const jsonColumns = safeParse(effective.standardMask.columns, {});
                    if (layoutAttributesIncludeId(attributes) && attributes.length > 1) {
                      try { table.setColumnLayout?.(attributes); } catch (_) {}
                    }
                    if (jsonColumns && typeof jsonColumns === 'object') {
                      const merged = mergeVisibility(jsonColumns);
                      setColumnsVisible(merged);
                      applyDataColumnVisibilityToTable(table, merged);
                    }
                    if (restartRestore) restoreHeaderFilters(table, confirmedQueryRef.current.filters);
                  } finally {
                    table.restoreRedraw?.(true);
                  }
                } else {
                  applyDataColumnVisibilityToTable(table, DEFAULT_COLUMNS_VISIBLE);
                  if (restartRestore) restoreHeaderFilters(table, confirmedQueryRef.current.filters);
                }
                applyQualityPartnerListLocale(table, t);
                tableBuiltRef.current = true;
                restartQueuedRef.current = false;
                snapshotConfirmedQuery();
                setTimeout(() => {
                  isRestoringRef.current = false;
                  sessionQueryActiveRef.current = false;
                }, 0);
              },
              headerFilterChanged: () => {
                if (isRestoringRef.current) return;
                const next = coerceHeaderFilterList(getTable()?.getHeaderFilters?.());
                if (byCompanyHeaderFiltersEquivalent(next, confirmedQueryRef.current.filters)) return;
                userInteractedRef.current = true;
                requestFullProgressiveSessionRestart();
              },
              sortChanged: () => {
                if (isRestoringRef.current) return;
                const next = cleanSorters(getTable()?.getSorters?.() || []);
                userInteractedRef.current = true;
                if (byCompanySortersEquivalent(next, confirmedQueryRef.current.sorters)) return;
                requestFullProgressiveSessionRestart();
              },
              dataSorted: () => {
                if (!isRestoringRef.current) userInteractedRef.current = true;
              },
              columnMoved: () => {
                if (!userInteractedRef.current || isRestoringRef.current) return;
                setUserMaskFilter(PAGE_LIST_KEY, 'column_move', null, null, getTable()?.getColumnLayout?.());
              },
              columnResized: () => {
                if (!userInteractedRef.current || isRestoringRef.current) return;
                setUserMaskFilter(PAGE_LIST_KEY, 'column_resized', null, null, getTable()?.getColumnLayout?.());
              },
              renderComplete: () => restoreHScroll(),
            }}
            options={{
              index: 'id',
              renderHorizontal: 'virtual',
              layout: 'fitColumns',
              autoResize: true,
              rowHeight: TABULATOR_AUDIT_MIN_ROW_HEIGHT,
              movableColumns: true,
              headerSortTristate: true,
              ...(cleanCalibrationByCompanySorters(confirmedQueryRef.current.sorters || []).length > 0
                ? {
                  initialSort: cleanCalibrationByCompanySorters(confirmedQueryRef.current.sorters).map((sorter) => ({
                    column: sorter.field,
                    dir: sorter.dir,
                  })),
                }
                : { initialSort: [{ column: 'name', dir: 'asc' }] }),
              ajaxURL: CALIBRATION_BY_COMPANY_PAGED_URL,
              ajaxParams: () => ({}),
              ajaxRequestFunc: (url, config, params) => {
                const generation = ajaxGenerationRef.current;
                return createDedupedAjaxRequest(getBuildRequestParams, tabulatorGet, inFlightRef, completedCacheRef, isRestoringRef, lastResponseRef)(url, config, params).then((data) => {
                  if (generation !== ajaxGenerationRef.current) {
                    return { data: [], last_page: data?.last_page ?? 1, total: 0, current_page: Number(params?.page) || 1 };
                  }
                  return data;
                });
              },
              dataLoader: true,
              dataLoaderLoading: t('Common.LoadingData'),
              dataLoaderError: t('Common.LoadFailed'),
              placeholder: t('Common.NoData'),
              height: 'calc(100vh - 193px)',
              progressiveLoad: 'scroll',
              progressiveLoadScrollMargin: 80,
              progressiveLoadDelay: 200,
              paginationSize: pageSizePc,
              filterMode: 'remote',
              sortMode: 'remote',
              pagination: false,
              ajaxRequesting: () => {
                if (tableBuiltRef.current && !isRestoringRef.current) captureHScroll();
                const table = getTable();
                if (table?.initialized) table.alert?.(t('Common.LoadingData'));
                return true;
              },
              ajaxResponse: (url, params, response) => {
                getTable()?.clearAlert?.();
                const next = applyPagedMutationOverlay(response, mutationOverlayRef.current);
          if (next && Array.isArray(next.data)) {
            next.data = decorateCalibrationByCompanyPagedRows(next.data);
          }
                if (next && typeof next === 'object') lastResponseRef.current = next;
                return next;
              },
              ajaxError: () => {
                getTable()?.clearAlert?.();
              },
              scrollToColumnIfVisible: false,
            }}
          />
        </div>
      </div>
      <Dialog header={t('Common.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-columns">
          <input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            type="text"
            className="block w-full px-4 py-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
          />
          <button type="button" onClick={() => searchTable()} className='w-16 ml-8 px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600'><SearchIcon /></button>
        </div>
      </Dialog>
      <Dialog header={t('Common.SetMask')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
        <div className="flex flex-col">
          <button type="button" onClick={() => { changeMarkings(!markUnmark); setMarkUnmarked(!markUnmark); }} className='m-1 px-4 py-2 text-center tracking-wide text-white transition-colors duration-200 transform bg-zinc-700 rounded-md hover:bg-zinc-500'>{markUnmark ? t('Common.DeselectAll') : t('Common.SelectAll')}</button>
          {columns.map(function (item, index) {
            if (!item?.field || !Object.prototype.hasOwnProperty.call(DEFAULT_COLUMNS_VISIBLE, item.field)) return null;
            return (
              <div key={item.field ?? index} className="flex flex-row border border-black rounded-md p-2 m-1">
                <Checkbox onChange={(e) => setMask(item.field, e.checked)} checked={columnsVisible[item.field]}></Checkbox><span className="ml-2">{item.title}</span>
              </div>
            );
          })}
          <button type="button" onClick={() => { setUserMaskFilter(PAGE_LIST_KEY, 'set_columns', columnsVisible, null, null); setMaskVisible(false); }} className='m-1 px-4 py-2 text-center tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600'>{t('Common.Save')}</button>
        </div>
      </Dialog>
      <PrintOptionsDialog
        visible={printChoiceVisible}
        onHide={() => setPrintChoiceVisible(false)}
        orientation={printOrientationChoice}
        onOrientationChange={setPrintOrientationChoice}
        layout={printLayoutChoice}
        onLayoutChange={setPrintLayoutChoice}
        onConfirm={runByCompanyPrint}
      />
    </div>
  );
}
